import React from "react";
import { Avatar } from "@narmi/design_system";
import styles from "./ThirdPartyDataAccess.module.scss";

type ConnectionLogoProps = {
  logoUrl: string | null;
  name: string;
  dataPartner: string;
  isRevoked?: boolean;
};

// OAuth/FDX connections store an NDS icon name (e.g. "database") in logo_url,
// while aggregators like Plaid supply a hosted image URL. Fallbacks are kept
// visually distinct per partner so the two connection types are
// distinguishable at a glance: OAuth apps render an NDS icon, Plaid renders the
// connection's initial.
const PLAID_DATA_PARTNER = "plaid";
const OAUTH_FALLBACK_ICON = "lock";

const isImageUrl = (value: string | null): value is string =>
  !!value && /^https?:\/\//.test(value);

/**
 * Renders a third-party connection's logo as a 40x40 NDS Avatar (size "m",
 * matching the Figma design). Plaid connections render a hosted image, falling
 * back to a tonal Avatar with the connection's initial. OAuth/FDX connections
 * render an NDS icon (from the application's configured icon name), falling back
 * to a generic lock icon.
 */
const ConnectionLogo = ({
  logoUrl,
  name,
  dataPartner,
  isRevoked = false,
}: ConnectionLogoProps) => {
  let avatar: React.ReactNode;

  if (isImageUrl(logoUrl)) {
    avatar = <Avatar size="m" kind="tonal" label={name} imgurl={logoUrl} />;
  } else if (logoUrl || dataPartner !== PLAID_DATA_PARTNER) {
    // OAuth/FDX icon name, or the OAuth fallback icon when none is configured.
    const iconName = logoUrl || OAUTH_FALLBACK_ICON;
    avatar = (
      <span className={styles["connection-logo-icon"]} role="img" aria-label={name}>
        <span className={`narmi-icon-${iconName}`} aria-hidden="true" />
      </span>
    );
  } else {
    avatar = (
      <Avatar
        size="m"
        kind="tonal"
        label={name}
        initials={name.charAt(0).toUpperCase()}
      />
    );
  }

  return isRevoked ? (
    <span className={styles["connection-logo-revoked"]}>{avatar}</span>
  ) : (
    avatar
  );
};

export default ConnectionLogo;
